import {Component, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, OnDestroy, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subscription} from 'rxjs';
import {Customer, DistributionCenter, DistributionCenterIdentificationCode, GetDistributionCentersResponse, TradingPartner} from '@app/_models/api';
import {IdentificationCodesDisplay} from '@app/_models/global';
import {ApiService} from '@app/_services/api.service';
import {ToastService} from '@app/_services/toast.service';
import {DistributionCenterService} from '@app/_services/master-data/distribution-centers.service';
import {SortableDirective, SortEvent} from '@app/_directives/sortable.directive';
import {TableSearchComponent} from '@app/_components/table-search/table-search.component';
import {TablePagerComponent} from '@app/_components/table-pager/table-pager.component';
import {DistributionCenterModalComponent} from '@app/master-data/distribution-centers/distribution-center-modal/distribution-center-modal.component';

@Component({
    selector: 'app-distribution-centers',
    templateUrl: './distribution-centers.component.html',
    styleUrl: '../../app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        FormsModule,
        AsyncPipe,
        SortableDirective,
        NgbPopover,
        DistributionCenterModalComponent
    ]
})
export class DistributionCentersComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('dcModal') private dcModal!: DistributionCenterModalComponent;

    total$: Observable<number>;
    customers: Customer[];
    customerId: string | undefined;
    tradingPartners: TradingPartner[] = [];
    readonly loading = signal(false);
    readonly loadFailed = signal(false);
    private loadSubscription: Subscription | undefined;
    private tradingPartnerId: string | undefined;

    constructor(
        private route: ActivatedRoute,
        private toastService: ToastService,
        private apiService: ApiService,
        public service: DistributionCenterService
    ) {
        this.customers = this.route.snapshot.data['resolvedData'].data;
        this.total$ = this.service.total$;
        this.service.searchTerm = '';

        const {customerId, tradingPartnerId} = this.route.snapshot.queryParams;
        if (customerId) {
            this.loadDistributionCenters(customerId, tradingPartnerId);
        }
    }

    ngOnDestroy() {
        this.loadSubscription?.unsubscribe();
    }

    onCustomerChange(event: Event) {
        this.service.searchTerm = '';
        const customerId = (event.target as HTMLSelectElement).value;
        if (!customerId) {
            this.loadSubscription?.unsubscribe();
            this.customerId = undefined;
            this.loading.set(false);
            this.loadFailed.set(false);
            return;
        }

        this.loadDistributionCenters(customerId);
    }

    retry() {
        this.loadDistributionCenters(this.customerId!, this.tradingPartnerId);
    }

    private loadDistributionCenters(customerId: string, tradingPartnerId?: string) {
        this.customerId = customerId;
        this.tradingPartnerId = tradingPartnerId;
        this.loadFailed.set(false);
        this.loading.set(true);
        this.loadSubscription?.unsubscribe();

        this.loadSubscription = this.apiService.getAllDistributionCenters(customerId).subscribe({
            next: (response: GetDistributionCentersResponse) => {
                this.tradingPartners = response.tradingPartners;
                this.service.data = response.distributionCenters;
                const tradingPartner = this.tradingPartners.find(tp => tp.id === tradingPartnerId);
                if (tradingPartner) {
                    this.service.searchTerm = tradingPartner.name;
                }
                this.service.refreshDataSource();
                this.loading.set(false);
            },
            error: () => {
                this.tradingPartners = [];
                this.service.data = [];
                this.service.refreshDataSource();
                this.loadFailed.set(true);
                this.loading.set(false);
                this.toastService.showDanger('Request Failed', 'Failed to load distribution centers. Please try again.');
            }
        });
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    onAdd() {
        this.dcModal.openAdd(this.customerId!, this.tradingPartners);
    }

    onEdit(element: DistributionCenter) {
        this.dcModal.openEdit(this.customerId!, element);
    }

    onSaved(saved: DistributionCenter) {
        const exists = this.service.data.some(dc => dc.id === saved.id);
        this.service.data = exists
            ? this.service.data.map(dc => dc.id === saved.id ? saved : dc)
            : this.service.data.concat(saved);
        this.service.refreshDataSource();
    }

    getAddress(element: DistributionCenter) {
        return `${element.address1} ${element.city}, ${element.state} ${element.zipCode}`;
    }

    getIdentifierDisplay(identificationCode: DistributionCenterIdentificationCode) {
        const qualifier = IdentificationCodesDisplay[identificationCode.qualifier] ?? identificationCode.qualifier;
        return `${qualifier}: ${identificationCode.value}`;
    }
}
